import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { usersApi, rolesApi } from '@/services/api';
import type { SystemUser } from '@/types/user';
import type { Role } from '@/types/roles';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import {
  Button as KitButton, FilterSelect, FilterOption, Pill, Tag, ActiveStatus, RowActionMenu, confirmColors,
} from '@/components/ui';
import styles from './UserList.module.css';

const PAGE_SIZE = 20;

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ── Main component ───────────────────────────────────────────────────────────

const UserList: React.FC = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<SystemUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('created_ts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [filterRole, setFilterRole] = useState<Role | null>(null);
  const [filterStatus, setFilterStatus] = useState('');

  const roleOptions: FilterOption[] = useMemo(
    () => roles.map(r => ({ value: String(r.id), label: r.name })),
    [roles],
  );

  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    rolesApi.getRoles().then(setRoles).catch((err) => {
      console.error('Failed to load roles for filter:', err);
    });
  }, []);

  const debounceTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(query);
      setCurrentPage(1);
    }, 400);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ordering = sortDir === 'desc' ? `-${sortField}` : sortField;
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: PAGE_SIZE,
        search: debouncedSearch,
        ordering,
      };
      if (filterRole) params.role = filterRole.id;
      if (filterStatus !== '') params.is_active = filterStatus === 'true';

      const response = await usersApi.getUsers(params);
      setUsers(response.results);
      setTotalCount(response.count);
    } catch {
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, sortField, sortDir, filterRole, filterStatus]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const handleEdit = useCallback((user: SystemUser) => {
    navigate(`/users/${user.id}/edit`);
  }, [navigate]);

  const handleToggleStatus = useCallback(async (user: SystemUser) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} user?`,
      text: `Are you sure you want to ${action} ${user.full_name || user.email}?`,
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors(user.is_active ? 'danger' : 'success'),
      confirmButtonText: `Yes, ${action}`,
    });
    if (!result.isConfirmed) return;

    try {
      setActionLoadingId(user.id);
      await usersApi.toggleActive(user.id);
      toast.success(`User ${action}d successfully.`);
      fetchUsers();
    } catch {
      toast.error(`Failed to ${action} user.`);
    } finally {
      setActionLoadingId(null);
    }
  }, [fetchUsers]);

  const clearFilters = () => {
    setFilterRole(null);
    setFilterStatus('');
    setCurrentPage(1);
  };

  // ── Columns ──────────────────────────────────────────────────────────────

  const columns = useMemo<ColumnDef<SystemUser>[]>(() => [
    {
      key: 'full_name',
      header: 'Name',
      sortable: true,
      width: '18%',
      render: (user) => <span className={styles['user-name']}>{user.full_name || '—'}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      width: '22%',
      render: (user) => <span className={styles['user-email']}>{user.email}</span>,
    },
    {
      key: 'roles',
      header: 'Roles',
      sortable: false,
      width: '22%',
      render: (user) =>
        user.roles.length > 0 ? (
          <div className={styles['role-chips']}>
            {user.roles.map((role) => (
              <Tag key={role.id}>{role.name}</Tag>
            ))}
          </div>
        ) : (
          <span className={styles['no-roles']}>No roles</span>
        ),
    },
    {
      key: 'is_superuser',
      header: 'Superuser',
      sortable: true,
      width: '10%',
      render: (user) => (
        <Pill tone={user.is_superuser ? 'warning' : 'neutral'}>
          {user.is_superuser ? 'Yes' : 'No'}
        </Pill>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      width: '10%',
      render: (user) => (
        <ActiveStatus active={user.is_active} />
      ),
    },
    {
      key: 'created_ts',
      header: 'Created',
      sortable: true,
      width: '12%',
      render: (user) => (
        <span className={styles['date-cell']}>
          {new Date(user.created_ts).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      width: '6%',
      render: (user) => {
        const isLoading = actionLoadingId === user.id;
        return (
          <RowActionMenu
            label={`Actions for ${user.full_name || user.email}`}
            actions={[
              {
                label: 'Edit',
                icon: <EditIcon fontSize="small" />,
                disabled: isLoading,
                onClick: () => handleEdit(user),
              },
              {
                key: 'toggle-status',
                label: user.is_active ? 'Deactivate' : 'Activate',
                icon: user.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />,
                tone: user.is_active ? 'danger' : 'positive',
                loading: isLoading,
                onClick: () => handleToggleStatus(user),
              },
            ]}
          />
        );
      },
    },
  ], [actionLoadingId, handleEdit, handleToggleStatus]);

  return (
    <DataTable
      title="User Management"
      subtitle="System users, the roles they hold, and whether they can sign in."
      headerActions={
        <KitButton variant="primary" onClick={() => navigate('/users/new')}>
          Add New User
        </KitButton>
      }
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      searchPlaceholder="Search by name or email…"
      activeFilterCount={[filterRole, filterStatus].filter(Boolean).length}
      onClearFilters={clearFilters}
      filterSlot={
        <>
          <FilterSelect
            label="Role"
            options={roleOptions}
            value={roleOptions.find(o => o.value === String(filterRole?.id)) ?? null}
            onChange={(opt) => {
              setFilterRole(roles.find(r => String(r.id) === opt?.value) ?? null);
              setCurrentPage(1);
            }}
          />

          <FilterSelect
            label="Status"
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find(o => o.value === filterStatus) ?? null}
            onChange={(opt) => { setFilterStatus(opt?.value ?? ''); setCurrentPage(1); }}
          />
        </>
      }
      data={users}
      columns={columns}
      rowKey={(u) => u.id}
      totalCount={totalCount}
      loading={loading}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={PAGE_SIZE}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      emptyMessage="No users found matching your criteria."
    />
  );
};

export default UserList;
